import { useNavigate } from 'react-router-dom';
import { useI18n } from '../i18n';
import { useNotifyPrefs } from '../hooks/useNotifyPrefs';
import { useSession } from '../hooks/useSession';
import { PrefRow } from '../components/Inbox';
import { Button, Card, ListSkeleton, Notice, PageHeader, SectionTitle } from '../components/ui';

/**
 * Notification settings (W18): what we tell you about (orders always on — the switch is locked
 * with the reason; offers and reminders can be switched off) and where offers / reminders go
 * (push, SMS, WhatsApp). Every switch saves at once and rolls back if the save fails.
 */
export function NotifyPrefsPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const session = useSession();
  const p = useNotifyPrefs(session.signedIn, {
    saved: t('prefs.saved'),
    failed: t('prefs.failed'),
  });
  const prefs = p.prefs;

  return (
    <div className="min-h-dvh bg-page pb-10">
      <PageHeader
        title={t('prefs.title')}
        onBack={() => navigate(-1)}
        backLabel={t('common.back')}
      />
      <div className="flex flex-col gap-5 px-4 pt-4">
        {p.failed ? (
          <Notice
            tone="negative"
            supportRef
            title={t('common.wrong')}
            action={
              <Button size="sm" tone="outline" onClick={p.retry}>
                {t('common.retry')}
              </Button>
            }
          >
            {p.error}
          </Notice>
        ) : p.loading || !prefs ? (
          <Card>
            <ListSkeleton rows={4} />
          </Card>
        ) : (
          <>
            <section>
              <SectionTitle>{t('prefs.categories')}</SectionTitle>
              <Card flush>
                <div className="divide-y divide-hair">
                  <PrefRow
                    title={t('notify.cat.orders')}
                    hint={t('prefs.ordersHint')}
                    on={prefs.orders}
                    disabled
                    lockedNote={t('prefs.ordersLocked')}
                    onChange={() => undefined}
                  />
                  <PrefRow
                    title={t('notify.cat.offers')}
                    hint={t('prefs.offersHint')}
                    on={prefs.offers}
                    disabled={p.saving}
                    onChange={(next) => p.set('offers', next)}
                  />
                  <PrefRow
                    title={t('notify.cat.reminders')}
                    hint={t('prefs.remindersHint')}
                    on={prefs.reminders}
                    disabled={p.saving}
                    onChange={(next) => p.set('reminders', next)}
                  />
                </div>
              </Card>
            </section>

            <section>
              <SectionTitle>{t('prefs.channels')}</SectionTitle>
              <p className="-mt-1 mb-2.5 text-xs text-ink-2">{t('prefs.channelsHint')}</p>
              <Card flush>
                <div className="divide-y divide-hair">
                  <PrefRow
                    title={t('prefs.push')}
                    on={prefs.push}
                    disabled={p.saving}
                    onChange={(next) => p.set('push', next)}
                  />
                  <PrefRow
                    title={t('prefs.sms')}
                    on={prefs.sms}
                    disabled={p.saving}
                    onChange={(next) => p.set('sms', next)}
                  />
                  <PrefRow
                    title={t('prefs.whatsapp')}
                    on={prefs.whatsapp}
                    disabled={p.saving}
                    onChange={(next) => p.set('whatsapp', next)}
                  />
                </div>
              </Card>
            </section>

            <Notice tone="info" title={t('prefs.quiet')} />
          </>
        )}
      </div>
    </div>
  );
}
